import { formatPercent, unFormatPercent } from 'utils';

export const getOverrideValue = (value: any, divideBy = 100) => {
  return Number(unFormatPercent(value)) / divideBy;
};

export const initialiseOverrides = (investment: any, format = true) => {
  let defaultOverrides = [];
  if (investment.model_portfolio) {
    defaultOverrides = investment.model_portfolio.fund_allocations || [];
    defaultOverrides = defaultOverrides.flatMap((allocation: any) => {
      if (allocation.model_portfolio_definition) {
        return allocation.model_portfolio_definition.fund_allocations.map(
          (innerAllocation: any) => ({
            fund: innerAllocation.fund,
            ocf: null,
            trading_costs: null
          })
        );
      }

      return {
        fund: allocation.fund,
        ocf: null,
        trading_costs: null
      };
    });
  } else {
    defaultOverrides = [
      { fund: investment.fund, ocf: null, trading_costs: null }
    ];
  }

  return defaultOverrides.map((defaultOverride: any) => {
    const baseOverride =
      (investment.fund_charge_overrides &&
        investment.fund_charge_overrides.find((override: any) => {
          return override.fund.id === defaultOverride.fund.id;
        })) ||
      defaultOverride;
    return {
      ...baseOverride,
      ocf: baseOverride.ocf
        ? format
          ? formatPercent(Number(baseOverride.ocf) * 100)
          : Number(baseOverride.ocf)
        : null,
      trading_costs: baseOverride.trading_costs
        ? format
          ? formatPercent(Number(baseOverride.trading_costs) * 100)
          : Number(baseOverride.trading_costs)
        : null
    };
  });
};

export const getOverriddenCharges = (
  overrides: any,
  modelPortfolio: any,
  ocf: number,
  tradingCosts: number,
  divideBy = 100
) => {
  const isModelPortfolio = !!modelPortfolio;
  let overriddenOcf: number | null = null;
  let overriddenTradingCosts: number | null = null;

  if (isModelPortfolio && overrides.length) {
    modelPortfolio.fund_allocations
      .flatMap((allocation: any) => {
        if (allocation.fund) {
          return allocation;
        }

        return allocation.model_portfolio_definition.fund_allocations.map(
          (innerAllocation: any) => ({
            ...innerAllocation,
            allocation:
              Number(innerAllocation.allocation) * Number(allocation.allocation)
          })
        );
      })
      .forEach((allocation: any) => {
        const override = overrides.find((override: any) => {
          return (
            allocation.fund &&
            override.fund &&
            override.fund.id === allocation.fund.id
          );
        });

        const investment =
          allocation.fund || allocation.model_portfolio_selection;

        if (overriddenOcf === null) {
          overriddenOcf = 0;
        }

        overriddenOcf +=
          Number(
            override && override.ocf
              ? getOverrideValue(override.ocf, divideBy)
              : investment.ocf
          ) * Number(allocation.allocation);

        if (overriddenTradingCosts === null) {
          overriddenTradingCosts = 0;
        }

        overriddenTradingCosts +=
          Number(
            override && override.trading_costs
              ? getOverrideValue(override.trading_costs, divideBy)
              : investment.trading_costs
          ) * Number(allocation.allocation);
      });
  }

  const override = isModelPortfolio ? null : overrides && overrides[0];

  const fundOcf =
    (isModelPortfolio
      ? overriddenOcf
      : override
        ? getOverrideValue(override.ocf, divideBy)
        : ocf) || ocf;

  const fundTradingCosts =
    (isModelPortfolio
      ? overriddenTradingCosts
      : override
        ? getOverrideValue(override.trading_costs, divideBy)
        : tradingCosts) || tradingCosts;

  const customTotalFundCharge = Number(fundTradingCosts) + Number(fundOcf) || 0;

  return { fundOcf, fundTradingCosts, customTotalFundCharge };
};

export const combineCharges = (
  existingCharges: any,
  recommendedCharges: any
) => {
  return [
    ...existingCharges.map((charge: any) => ({
      ...charge,
      portfolio_type: 'existing',
      type: charge.type === 'overall' ? 'Existing Overall' : charge.type
    })),
    existingCharges.length > 0 && recommendedCharges.length > 0
      ? { type: 'gap' }
      : null,
    ...recommendedCharges.map((charge: any) => ({
      ...charge,
      portfolio_type: 'recommended',
      type: charge.type === 'overall' ? 'Recommended Overall' : charge.type
    }))
  ].filter(Boolean);
};

export const combinePolicyRiskScores = (
  existingPoliciesRiskScore: any,
  recommendedPoliciesRiskScore: any
) => {
  return [
    ...existingPoliciesRiskScore.map((item: any) => ({
      ...item,
      portfolio_type: 'existing'
    })),
    ...recommendedPoliciesRiskScore.map((item: any) => ({
      ...item,
      portfolio_type: 'recommended'
    }))
  ];
};
